import { isValidImage, isValidVideo, MaterialIcon, MaterialIconName } from '@gorila-shared-ui/components';
import { memo, useEffect } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { gptAlarmTypeIconState, gptAlarmTypeState } from '../../../storage/gptStates';
import { capturingTimelineEventState, capturingTimelineMapEventState } from '../../../storage/streaming';
import { isValidSafegptAlarmPoint } from '../../../utils/map';
import SafeGptAlarmIcon from '../../shared/SafeGptAlarmIcon';

function CapturingVideoEventMarker() {
  const mapEvent = useRecoilValue(capturingTimelineMapEventState);
  const resetMapEvent = useResetRecoilState(capturingTimelineMapEventState);
  const setEvent = useSetRecoilState(capturingTimelineEventState);
  const { css, theme, concatenateClasses } = useStyles();
  const hasValidAlarmType = mapEvent?.alarmType && mapEvent.alarmType > -1;
  const alarmData = useRecoilValue(gptAlarmTypeState(mapEvent?.alarmType));
  const alarmIcon = useRecoilValue(gptAlarmTypeIconState(mapEvent?.alarmType));
  const iconWrapper = css({
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 0,
    background: hasValidAlarmType && alarmData?.color ? alarmData.color : theme.colors.backgroundInversePrimary,
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    boxSizing: 'border-box',
    paddingTop: theme.sizing.scale200,
    paddingLeft: theme.sizing.scale200,
    paddingRight: theme.sizing.scale200,
    paddingBottom: theme.sizing.scale200,
  });

  useEffect(() => {
    if (mapEvent) {
      const timeout = setTimeout(() => {
        resetMapEvent();
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [mapEvent]);

  if (!mapEvent || !isValidSafegptAlarmPoint(mapEvent.longitude, mapEvent.latitude)) {
    return null;
  }

  const { latitude, longitude } = mapEvent;

  const onClick = () => {
    setEvent(mapEvent);
  };

  const eventIcon = ((): MaterialIconName => {
    if (hasValidAlarmType) {
      return alarmIcon ? '' : 'warning';
    }
    if (mapEvent.key) {
      if (isValidImage(mapEvent.key)) {
        return 'image';
      }
      if (isValidVideo(mapEvent.key)) {
        return 'videocam';
      }
    }
    return 'place';
  })();

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      rotation={0}
      onClick={onClick}
    >
      <span
        className={concatenateClasses(
          iconWrapper,
          css({
            animation: 'pulse 2s infinite',
          }),
        )}
      >
        {!eventIcon && alarmIcon && (
          <SafeGptAlarmIcon
            url={alarmIcon}
            size={theme.sizing.scale650}
            color={theme.colors.contentInversePrimary}
          />
        )}
        {!alarmIcon && (
          <MaterialIcon
            name={eventIcon}
            size={theme.sizing.scale650}
            color={theme.colors.contentInversePrimary}
          />
        )}
      </span>
    </Marker>
  );
}

export default memo(CapturingVideoEventMarker);
